/**
 * Scoreboard aggregation: M4 batch 6c.
 *
 * Pure helper that walks `turnSummaries` and rolls up per-player totals
 * for the gameOver scoreboard. Two streams of points:
 *
 *   - **Drawer points**: `summary.drawerPoints` credits the
 *     `summary.drawerId`. Per PRD §3.8 drawer-disconnect aborts zero
 *     this out; we accept whatever the reducer produced.
 *   - **Audience points**: each entry in `summary.guesses` credits its
 *     `playerId` (added in M4 batch 6c commit 1; before that, the
 *     summary only knew the first guesser's id).
 *
 * Players who never scored still appear in the result (0/0/0), the
 * scoreboard reflects the full roster, so the winner-vs-also-rans
 * narrative stays intact even for players who watched every turn.
 *
 * Players who have left the room (Q9, audit 2026-06-11): when a
 * `departedNames` map is supplied, ids that scored in `turnSummaries`
 * but are gone from the `players` roster are KEPT, flagged `left: true`,
 * and named from the map ("Someone" when even the name is unknown,
 * e.g. a reload straight into gameOver). Pre-fix they silently vanished
 * from the standings, points and all. Callers that pass no map (the
 * players panel via buildTurnScores) keep the old roster-only behavior.
 *
 * Sort: descending by `total`; ties broken alphabetically by `name`
 * (stable secondary key: `Array.prototype.sort` is stable in modern
 * V8 / SpiderMonkey).
 */

import type { Player, PlayerId, TurnSummary } from "@drawlol/shared";

export type ScoreboardEntry = {
  playerId: PlayerId;
  name: string;
  drawerPoints: number;
  audiencePoints: number;
  total: number;
  /** True when the player left before game end (Q9, audit 2026-06-11). */
  left: boolean;
};

/**
 * Aggregate per-player totals from a list of turn summaries.
 *
 * @param turnSummaries Completed-turn rollups, in turn order.
 * @param players       Current room roster (post-`PROMOTE_SPECTATORS`).
 * @param departedNames Optional id → display-name memory for players no
 *                      longer in the roster (RoomShell accumulates it
 *                      from every roster it has seen). When provided,
 *                      departed scorers are kept with `left: true`;
 *                      when omitted, they're dropped (roster-only view).
 * @returns Entries sorted descending by `total`, then alphabetically
 *          by `name` as the deterministic tiebreaker.
 */
export function aggregateScoreboard(
  turnSummaries: ReadonlyArray<TurnSummary>,
  players: ReadonlyMap<PlayerId, Pick<Player, "id" | "name">>,
  departedNames?: ReadonlyMap<PlayerId, string>,
): ReadonlyArray<ScoreboardEntry> {
  const drawer = new Map<PlayerId, number>();
  const audience = new Map<PlayerId, number>();

  // Seed both maps with the current roster so players who never scored
  // still show up at 0/0/0.
  for (const id of players.keys()) {
    drawer.set(id, 0);
    audience.set(id, 0);
  }

  // Whether a scoring id is kept: roster members always; departed ids only
  // in the departed-aware view (Q9).
  const keep = (id: PlayerId): boolean => players.has(id) || departedNames !== undefined;

  for (const summary of turnSummaries) {
    if (keep(summary.drawerId)) {
      drawer.set(summary.drawerId, (drawer.get(summary.drawerId) ?? 0) + summary.drawerPoints);
    }
    for (const g of summary.guesses) {
      if (keep(g.playerId)) {
        audience.set(g.playerId, (audience.get(g.playerId) ?? 0) + g.points);
      }
    }
  }

  const entries: ScoreboardEntry[] = [];
  const seen = new Set<PlayerId>();
  for (const [id, player] of players) {
    seen.add(id);
    const dp = drawer.get(id) ?? 0;
    const ap = audience.get(id) ?? 0;
    entries.push({
      playerId: id,
      name: player.name,
      drawerPoints: dp,
      audiencePoints: ap,
      total: dp + ap,
      left: false,
    });
  }
  // Departed scorers: ids in the score maps but not the roster.
  for (const id of new Set([...drawer.keys(), ...audience.keys()])) {
    if (seen.has(id)) continue;
    const dp = drawer.get(id) ?? 0;
    const ap = audience.get(id) ?? 0;
    entries.push({
      playerId: id,
      name: departedNames?.get(id) ?? "Someone",
      drawerPoints: dp,
      audiencePoints: ap,
      total: dp + ap,
      left: true,
    });
  }

  entries.sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  });

  return entries;
}

/**
 * Standard competition ranking ("1224"): entries with an equal score SHARE
 * the same rank, and the rank after a tie skips to the ordinal position of
 * the next distinct score. So [3,2,2,1] → [1,2,2,4], and a two-way tie at the
 * top → [1,1].
 *
 * Fixes the "tie shows as 1./2." bug (audit 2026-07-02): both the gameOver
 * table and the reveal standings previously used the raw array index (`i+1`)
 * as the rank, contradicting the "It's a tie" banner. Callers pass the
 * per-row `total`s in the SAME descending order `aggregateScoreboard`
 * produces; the result is parallel to the input.
 *
 * Pure function: order-preserving, no sorting of its own.
 */
export function competitionRanks(scores: ReadonlyArray<number>): number[] {
  const ranks: number[] = [];
  for (let i = 0; i < scores.length; i++) {
    if (i > 0 && scores[i] === scores[i - 1]) {
      ranks.push(ranks[i - 1]!);
    } else {
      ranks.push(i + 1);
    }
  }
  return ranks;
}
